import { put, select, takeEvery } from "redux-saga/effects";
import {
    getIsLocationInitedSelector,
    getLocationOriginPathnameSelector,
    getLocationOriginSearchSelector,
    getLocationPlatformSelector
} from "./locationSelectors";
import { locationSlice } from "./locationReducer";
import { MobileRouterPaths } from "../../mobileAppCore/mobileAppConstants";
import appHistory from "../../app/appHistory";
import { IAddLocationPayload, ILocationPlatformConfig, IOpenLocationPayload, TLocationPlatform } from "./location.t";
import { getCurrentPlatformRedirectPath } from "./utils/getCurrentPlatformRedirectPath";
import { LOCATION_CONFIGS, LOCATION_NAMES, LOCATION_PLATFORM } from "./locationConstants";
import { getAppFeatures } from "../../app/appSelectors";
import { FEATURES, LOG_EVENT_TYPES } from "../../app/appConstants";
import { appLog } from "../../app/appActions";

export function* locationOpenOriginPageSaga() {
    const locationOriginPathname = yield select(getLocationOriginPathnameSelector);
    const locationOriginSearch = yield select(getLocationOriginSearchSelector);
    yield put(locationSlice.actions.setOriginParamsAction({
        originPathname: '',
        originSearch: '',
    }));

    if (!locationOriginPathname && !locationOriginSearch) {
        yield put(locationSlice.actions.openLocationAction({ name: LOCATION_NAMES.HOME }));
        return;
    }

    const isOriginPageOpened = appHistory.location.pathname === locationOriginPathname;
    const pathname = isOriginPageOpened
        ? MobileRouterPaths.default
        : locationOriginPathname || MobileRouterPaths.default;
    const search = locationOriginSearch || '';

    const platform = yield select(getLocationPlatformSelector);
    const redirectUrl = getCurrentPlatformRedirectPath(platform, pathname, search);

    appHistory.replace(MobileRouterPaths.default);
    appHistory.push(redirectUrl || `${pathname}${search}`);
}

export function* updatePlatformSaga({ payload: currentPlatform }: { payload: TLocationPlatform }) {
    const features = yield select(getAppFeatures);
    const redirectUrl = (currentPlatform === LOCATION_PLATFORM.DESKTOP_DX || !features[FEATURES.DESKTOP])
        ? ''
        : getCurrentPlatformRedirectPath(currentPlatform);
    if (redirectUrl) {
        appHistory.replace(redirectUrl);
    }
    const isLocationInited = yield select(getIsLocationInitedSelector);
    if (!isLocationInited) {
        yield put(locationSlice.actions.setIsInitedAction(true));
    }
}

export function* openLocationSaga({ payload }: { payload: IOpenLocationPayload }) {
    const { name, searchParams, state } = payload;
    const locationPlatform: TLocationPlatform = yield select(getLocationPlatformSelector);
    const locationConfig = LOCATION_CONFIGS[name];
    if (!locationConfig) {
        yield put(
            appLog({
                type: LOG_EVENT_TYPES.exception,
                payload: {
                    error: new Error(`Location config not found, name: ${name}, platform: ${locationPlatform}`),
                    stack: `Location config not found, name: ${name}, platform: ${locationPlatform}`,
                    string: `Location config not found, name: ${name}, platform: ${locationPlatform}`,
                    stringify: `Location config not found, name: ${name}, platform: ${locationPlatform}`,
                }
            })
        )
        return;
    }
    const locationPlatformConfig: ILocationPlatformConfig = locationConfig[locationPlatform] || locationConfig as ILocationPlatformConfig;
    const newLocation = `${locationPlatformConfig.pathname || appHistory.location.pathname}?${new URLSearchParams({
        ...Object.fromEntries(new URLSearchParams(locationPlatformConfig.search || '')),
        ...searchParams,
    }).toString()}`;

    if (payload.method === 'replace') {
        appHistory.replace(newLocation, state);
    } else {
        appHistory.push(newLocation, state);
    }
}

export function* addLocationSaga({ payload }: { payload: IAddLocationPayload }) {
    const { name, searchParams, state } = payload;
    const locationPlatform: TLocationPlatform = yield select(getLocationPlatformSelector);
    const locationConfig = LOCATION_CONFIGS[name];
    if (!locationConfig) {
        yield put(
            appLog({
                type: LOG_EVENT_TYPES.exception,
                payload: {
                    error: new Error(`Location config not found, name: ${name}, platform: ${locationPlatform}`),
                    stack: `Location config not found, name: ${name}, platform: ${locationPlatform}`,
                    string: `Location config not found, name: ${name}, platform: ${locationPlatform}`,
                    stringify: `Location config not found, name: ${name}, platform: ${locationPlatform}`,
                }
            })
        )
        return;
    }
    const locationPlatformConfig: ILocationPlatformConfig = locationConfig[locationPlatform] || locationConfig as ILocationPlatformConfig;
    const urlParams = new URLSearchParams(window.location.search);
    const locationConfigUrlParams = new URLSearchParams(locationPlatformConfig.search || '');
    const urlSearchParams = new URLSearchParams(searchParams);

    locationConfigUrlParams.forEach((value, key) => {
        urlParams.append(key, value);
    });
    urlSearchParams.forEach((value, key) => {
        urlParams.append(key, value);
    });

    appHistory.push(`${locationPlatformConfig.pathname || appHistory.location.pathname}?${urlParams.toString()}`, state)
}

export function* locationSagas() {
    yield takeEvery(locationSlice.actions.openOriginPageAction, locationOpenOriginPageSaga);
    yield takeEvery(locationSlice.actions.updatePlatformAction, updatePlatformSaga);
    yield takeEvery(locationSlice.actions.openLocationAction, openLocationSaga);
    yield takeEvery(locationSlice.actions.addLocationAction, addLocationSaga);
}
